/* Folds the job event stream into one state object (pure; see job.test.tsx and job-events.test.ts).
   Accepts both shapes of every event: the API's (`@cx/shared` events.ts: step `title`, `callId`, flat
   citation/artifact/approval with string tiers, `succeeded`, `costCredits`) and the client's (label,
   toolCallId, nested objects, numeric tiers, `completed`, `credits`), because each event is normalized with
   the shared `toClientEvent` first (idempotent, so events already normalized by sse.ts are unchanged).
   Besides the chat-shaped fields it keeps what modules send: `output` (run.finished), `progress` and
   `research` (state.snapshot), approval `details` / `expiresAt` / `allowTask`, voice session events
   (`voice`) and the last payload of any other non-vocabulary event (`module`). */
import { toClientEvent } from '../../../shared/src/normalize.ts';
import type {
  AnyJobEvent, Approval, ApprovalDecision, Artifact, Citation, JobStatus, RiskTier, StepStatus, Usage,
} from './api-types';
import { EVENT_TYPES } from './api-types';

export interface ToolCallState {
  id: string;
  name: string;
  label?: string;
  argsText: string;
  args?: unknown;
  result?: unknown;
  isError?: boolean;
  status: StepStatus;
  stepId: string;
  durationMs?: number;
}

export interface StepState {
  id: string;
  label: string;
  kind?: string;
  target?: string;
  status: StepStatus;
  startedAt?: number;
  durationMs?: number;
  summary?: string;
  toolCallIds: string[];
}

export interface ApprovalState extends Approval {
  status: 'pending' | ApprovalDecision;
  by?: string;
  /** The API's outcome word once resolved: allow | allow_task | deny | timeout | cancelled. */
  outcome?: string;
  editedPreview?: string;
}

export interface ArtifactState extends Artifact {
  subtype?: string;
  versions: { version: number; content?: string }[];
}

/** Progress a module reports in `state.snapshot` (`{progress: 0-100, phase, note, paused}`). */
export interface JobProgress {
  percent: number | null;
  phase?: string;
  note?: string;
  paused?: boolean;
}

/** Research runs (`research.deep` / `research.wide`) send this as their snapshot state. */
export interface ResearchSnapshot {
  runId?: string;
  kind?: 'deep' | 'wide' | string;
  phase?: string;
  progress?: number;
  sourcesConsulted?: number;
  sourcesCited?: number;
  sourcesDiscarded?: number;
  subQuestionsDone?: number;
  subQuestionsTotal?: number;
  itemsDone?: number;
  itemsTotal?: number;
  creditsUsed?: number;
  maxCredits?: number | null;
  paused?: boolean;
  activity?: { domain?: string; title?: string; action?: string; ts?: string }[];
  findings?: unknown[];
  [k: string]: unknown;
}

/** Voice session events (`voice.*`, from GET /api/v1/voice/sessions/:id/events). */
export interface VoiceFold {
  state?: string;
  listening?: boolean;
  partial?: string;
  transcripts: { turnId?: string; role: string; text: string; language?: string }[];
  captions: string[];
  turnsFinished: number;
  costCredits?: number;
  warning?: string;
  error?: { code?: string; message: string };
  ended?: boolean;
}

export interface JobState {
  jobId?: string;
  kind?: string;
  status: JobStatus;
  cursor: number;
  text: string;
  textDone: boolean;
  reasoning: string;
  steps: StepState[];
  toolCalls: Record<string, ToolCallState>;
  citations: Citation[];
  artifacts: ArtifactState[];
  approvals: ApprovalState[];
  usage: Usage;
  model?: string;
  error?: { code: string; message: string; retryable?: boolean };
  /** The job's own state from the last `state.snapshot` (module-specific, e.g. {progress, note}). */
  serverState?: unknown;
  /** The server's job status from the last snapshot (queued, running, waiting_approval, ...). */
  serverStatus?: string;
  /** Module progress from the snapshot, when it has `progress` or `phase`. */
  progress?: JobProgress;
  /** Research snapshot (deep and wide runs). */
  research?: ResearchSnapshot;
  /** `run.finished.output`: what the job produced (ids, report, counts). */
  output?: unknown;
  voice?: VoiceFold;
  /** Last payload of every event outside the job vocabulary, by type. */
  module?: Record<string, unknown>;
  startedAt?: number;
  finishedAt?: number;
}

/** Any SSE envelope, including module events outside the job vocabulary (voice, etc.). */
export interface ModuleJobEvent { id: string; seq: number; ts: string | number; type: string; data: unknown }

export const initialJobState = (jobId?: string): JobState => ({
  jobId, status: 'idle', cursor: 0, text: '', textDone: false, reasoning: '', steps: [], toolCalls: {},
  citations: [], artifacts: [], approvals: [], usage: {},
});

const KNOWN: ReadonlySet<string> = new Set(EVENT_TYPES);
const tsOf = (ts: string | number): number => (typeof ts === 'number' ? ts : Date.parse(ts) || Date.now());
type Loose = Record<string, unknown>;
const isObj = (v: unknown): v is Loose => typeof v === 'object' && v !== null && !Array.isArray(v);
const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const round = (n: number) => Math.round(n * 10_000) / 10_000;
const TIER_RANK: Record<string, RiskTier> = { read: 0, change: 1, consequential: 2, sensitive: 3 };

/** Both shapes to the client shape (see the header). */
export function normalizeJobEvent(ev: AnyJobEvent | ModuleJobEvent): AnyJobEvent {
  return toClientEvent(ev as AnyJobEvent) as AnyJobEvent;
}

function upsertStep(steps: StepState[], id: string, patch: Partial<StepState> & { label?: string }): StepState[] {
  const i = steps.findIndex((s) => s.id === id);
  if (i < 0) return [...steps, { id, label: patch.label ?? id, status: 'running', toolCallIds: [], ...patch } as StepState];
  const next = steps.slice();
  next[i] = { ...steps[i]!, ...patch };
  return next;
}

function liveStatus(state: JobState): JobStatus {
  if (state.status === 'completed' || state.status === 'cancelled' || state.status === 'error') return state.status;
  return state.approvals.some((a) => a.status === 'pending') ? 'waiting' : 'running';
}

function approvalFrom(d: Loose): Approval {
  const nested = isObj(d.approval) ? (d.approval as unknown as Approval) : null;
  const tierName = str(d.tier) ?? (nested && typeof nested.tier === 'string' ? (nested.tier as string) : undefined);
  const tier: RiskTier = nested && typeof nested.tier === 'number' ? nested.tier : TIER_RANK[tierName ?? ''] ?? 2;
  const base: Approval = nested ? { ...nested, tier } : { id: str(d.approvalId) ?? 'approval', tier, summary: str(d.summary) ?? 'Approval needed' };
  const out: Approval = { ...base };
  if (tierName) out.tierName = tierName;
  if (d.details !== undefined && out.details === undefined) out.details = d.details;
  if (out.expiresAt === undefined && str(d.expiresAt)) out.expiresAt = str(d.expiresAt);
  if (typeof d.allowTask === 'boolean') out.allowTask = d.allowTask;
  // Tiers 2-3 never offer "allow for the rest of this task".
  if (out.tier >= 2) out.allowTask = false;
  if (out.preview === undefined && typeof d.preview === 'string') out.preview = d.preview;
  return out;
}

function snapshotFields(state: unknown): Pick<JobState, 'progress' | 'research'> {
  if (!isObj(state)) return {};
  const out: Pick<JobState, 'progress' | 'research'> = {};
  const percent = num(state.progress);
  const phase = str(state.phase);
  if (percent !== undefined || phase) {
    out.progress = { percent: percent ?? null, ...(phase ? { phase } : {}), ...(str(state.note) ? { note: str(state.note) } : {}), ...(typeof state.paused === 'boolean' ? { paused: state.paused } : {}) };
  }
  if (str(state.runId) && (state.kind === 'deep' || state.kind === 'wide' || 'sourcesConsulted' in state)) out.research = state as ResearchSnapshot;
  return out;
}

const VOICE_EMPTY: VoiceFold = { transcripts: [], captions: [], turnsFinished: 0 };

function foldVoice(v: VoiceFold, type: string, d: Loose): VoiceFold {
  switch (type) {
    case 'voice.session.started': return { ...v, ended: false };
    case 'voice.state': return { ...v, state: str(d.state) ?? v.state };
    case 'voice.listening': return { ...v, listening: d.listening === true };
    case 'voice.partial_transcript': return { ...v, partial: str(d.text) ?? '' };
    case 'voice.final_transcript': {
      const t = { turnId: str(d.turnId), role: str(d.role) ?? 'user', text: str(d.text) ?? '', language: str(d.language) };
      return { ...v, partial: undefined, transcripts: [...v.transcripts.filter((x) => !(x.turnId && x.turnId === t.turnId && x.role === t.role)), t] };
    }
    case 'voice.caption': return { ...v, captions: [...v.captions, str(d.text) ?? ''].slice(-50) };
    case 'voice.turn.finished': return { ...v, turnsFinished: v.turnsFinished + 1 };
    case 'voice.usage': return { ...v, costCredits: num(d.costCredits) ?? v.costCredits };
    case 'voice.warning': return { ...v, warning: str(d.message) ?? str(d.code) };
    case 'voice.error': return { ...v, error: { code: str(d.code), message: str(d.message) ?? 'Voice error' } };
    case 'voice.session.ended': return { ...v, ended: true, state: 'ended', listening: false };
    default: return v;
  }
}

function foldModuleEvent(s: JobState, ev: ModuleJobEvent): JobState {
  const d = isObj(ev.data) ? ev.data : {};
  let next: JobState = { ...s, module: { ...s.module, [ev.type]: ev.data } };
  if (ev.type.startsWith('voice.')) {
    next = { ...next, voice: foldVoice(s.voice ?? VOICE_EMPTY, ev.type, d) };
    if (ev.type === 'voice.session.started' && s.status === 'idle') next = { ...next, status: 'running', startedAt: s.startedAt ?? tsOf(ev.ts) };
    if (ev.type === 'voice.session.ended') next = { ...next, status: 'completed', finishedAt: tsOf(ev.ts) };
    if (ev.type === 'voice.error' && d.fatal === true) next = { ...next, status: 'error', error: { code: str(d.code) ?? 'voice_error', message: str(d.message) ?? 'Voice error' } };
  }
  return next;
}

export function foldEvent(state: JobState, raw: AnyJobEvent | ModuleJobEvent): JobState {
  if (raw.seq > 0 && raw.seq <= state.cursor) return state;
  const base: JobState = { ...state, cursor: raw.seq > 0 ? raw.seq : state.cursor };
  if (!KNOWN.has(raw.type)) return foldModuleEvent(base, raw as ModuleJobEvent);
  const ev = normalizeJobEvent(raw);
  const d = (isObj(ev.data) ? ev.data : {}) as Loose;
  let s = base;
  const at = tsOf(ev.ts);
  switch (ev.type) {
    case 'run.started':
      s = { ...s, status: s.status === 'idle' ? 'running' : s.status, startedAt: s.startedAt ?? at, jobId: ev.data.jobId ?? s.jobId, kind: ev.data.kind ?? s.kind, model: ev.data.model ?? s.model };
      break;
    case 'text.delta':
      s = { ...s, text: s.text + (ev.data.delta ?? ''), status: liveStatus(s) };
      break;
    case 'text.done':
      s = { ...s, text: typeof ev.data.text === 'string' && ev.data.text.length >= s.text.length ? ev.data.text : s.text, textDone: true };
      break;
    case 'reasoning.delta':
      s = { ...s, reasoning: s.reasoning + (ev.data.delta ?? '') };
      break;
    case 'step.started': {
      const label = ev.data.label ?? str(d.title) ?? ev.data.stepId;
      s = { ...s, steps: upsertStep(s.steps, ev.data.stepId, { label, kind: ev.data.kind, target: ev.data.target, status: 'running', startedAt: at }), status: liveStatus(s) };
      break;
    }
    case 'step.finished': {
      const prev = s.steps.find((x) => x.id === ev.data.stepId);
      const durationMs = ev.data.durationMs ?? (prev?.startedAt ? at - prev.startedAt : undefined);
      s = { ...s, steps: upsertStep(s.steps, ev.data.stepId, { status: ev.data.status ?? 'ok', durationMs, summary: ev.data.summary ?? prev?.summary }) };
      break;
    }
    case 'tool.call.started': {
      const id = ev.data.toolCallId ?? ev.data.callId ?? `call-${ev.seq}`;
      const stepId = ev.data.stepId ?? `tool:${id}`;
      const call: ToolCallState = { id, name: ev.data.name, label: ev.data.label, argsText: '', status: 'running', stepId };
      let steps = s.steps;
      const existing = steps.find((x) => x.id === stepId);
      steps = existing
        ? upsertStep(steps, stepId, { toolCallIds: [...existing.toolCallIds.filter((x) => x !== id), id], status: 'running' })
        : upsertStep(steps, stepId, { label: ev.data.label ?? ev.data.name, kind: 'tool', status: 'running', startedAt: at, toolCallIds: [id] });
      s = { ...s, toolCalls: { ...s.toolCalls, [id]: call }, steps };
      break;
    }
    case 'tool.call.args': {
      const call = s.toolCalls[ev.data.toolCallId ?? ev.data.callId ?? ''];
      if (!call) break;
      const argsText = call.argsText + (ev.data.delta ?? '');
      let args = ev.data.args ?? call.args;
      if (ev.data.args === undefined) { try { args = JSON.parse(argsText); } catch { /* still streaming */ } }
      s = { ...s, toolCalls: { ...s.toolCalls, [call.id]: { ...call, argsText, args } } };
      break;
    }
    case 'tool.call.result': {
      const call = s.toolCalls[ev.data.toolCallId ?? ev.data.callId ?? ''];
      if (!call) break;
      const isError = ev.data.isError ?? (ev.data.error !== undefined ? true : undefined);
      const status: StepStatus = isError ? 'error' : 'ok';
      const result = ev.data.result ?? ev.data.error;
      const updated = { ...call, result, isError, status, durationMs: ev.data.durationMs };
      const toolCalls = { ...s.toolCalls, [call.id]: updated };
      let steps = s.steps;
      const step = steps.find((x) => x.id === call.stepId);
      if (step && step.id.startsWith('tool:')) {
        steps = upsertStep(steps, step.id, { status, durationMs: ev.data.durationMs ?? (step.startedAt ? at - step.startedAt : undefined) });
      }
      s = { ...s, toolCalls, steps };
      break;
    }
    case 'citation.added': {
      const c = ev.data.citation;
      if (!c) break;
      const idx = s.citations.findIndex((x) => x.id === c.id);
      const citedCount = s.citations.filter((x) => x.cited !== false).length;
      const withN: Citation = { ...c, n: c.n ?? (c.cited === false ? undefined : citedCount + 1) };
      const citations = idx < 0 ? [...s.citations, withN] : s.citations.map((x, i) => (i === idx ? { ...x, ...c, n: x.n ?? withN.n } : x));
      s = { ...s, citations };
      break;
    }
    case 'artifact.created': {
      const a = ev.data.artifact;
      if (!a) break;
      const subtype = str(d.subtype);
      const art: ArtifactState = { ...a, version: a.version ?? 1, ...(subtype ? { subtype } : {}), versions: [{ version: a.version ?? 1, content: a.content }] };
      s = { ...s, artifacts: [...s.artifacts.filter((x) => x.id !== a.id), art] };
      break;
    }
    case 'artifact.updated': {
      const u = ev.data;
      const known = s.artifacts.some((a) => a.id === u.artifactId);
      const artifacts = known ? s.artifacts : [...s.artifacts, { id: u.artifactId, kind: 'document', title: u.title ?? u.artifactId, version: 0, versions: [] } as ArtifactState];
      s = {
        ...s,
        artifacts: artifacts.map((a) => {
          if (a.id !== u.artifactId) return a;
          const version = u.version ?? a.version + 1;
          return { ...a, version, title: u.title ?? a.title, content: u.content ?? a.content, versions: [...a.versions.filter((v) => v.version !== version), { version, content: u.content ?? a.content }] };
        }),
      };
      break;
    }
    case 'approval.requested': {
      const a = approvalFrom(d);
      const approvals = [...s.approvals.filter((x) => x.id !== a.id), { ...a, status: 'pending' as const }];
      let steps = s.steps;
      if (a.stepId && steps.some((x) => x.id === a.stepId)) steps = upsertStep(steps, a.stepId, { status: 'waiting' });
      s = { ...s, approvals, steps, status: s.status === 'completed' || s.status === 'cancelled' || s.status === 'error' ? s.status : 'waiting' };
      break;
    }
    case 'approval.resolved': {
      const r = ev.data;
      const outcome = r.outcome ?? (['allow', 'allow_task', 'deny', 'timeout', 'cancelled'].includes(String(r.decision)) ? String(r.decision) : undefined);
      const approvals = s.approvals.map((a) => (a.id === r.approvalId
        ? { ...a, status: r.decision, by: r.by, ...(outcome ? { outcome } : {}), ...(r.editedPreview !== undefined ? { editedPreview: r.editedPreview } : {}) }
        : a));
      const a = approvals.find((x) => x.id === r.approvalId);
      let steps = s.steps;
      if (a?.stepId && steps.some((x) => x.id === a.stepId)) {
        steps = upsertStep(steps, a.stepId, { status: r.decision === 'denied' || r.decision === 'expired' ? 'skipped' : 'running' });
      }
      s = { ...s, approvals, steps };
      s = { ...s, status: liveStatus(s) };
      break;
    }
    case 'usage.updated': {
      const u = ev.data;
      const credits = u.credits ?? u.costCredits;
      const inT = u.inputTokens ?? u.tokensIn;
      const outT = u.outputTokens ?? u.tokensOut;
      const prev = s.usage;
      const model = u.model && u.model !== 'unknown' ? u.model : undefined;
      s = {
        ...s,
        usage: {
          ...prev,
          ...(credits !== undefined ? { credits: round((prev.credits ?? 0) + credits) } : {}),
          ...(inT !== undefined ? { inputTokens: (prev.inputTokens ?? 0) + inT } : {}),
          ...(outT !== undefined ? { outputTokens: (prev.outputTokens ?? 0) + outT } : {}),
          ...(model ? { model } : {}),
          ...(u.provider && u.provider !== 'unknown' ? { provider: u.provider } : {}),
          ...(u.byok !== undefined ? { byok: u.byok } : {}),
          calls: (prev.calls ?? 0) + 1,
        },
        model: model ?? s.model,
      };
      break;
    }
    case 'state.snapshot': {
      // The API's snapshot carries the job's own resumable state (any shape); fold the fields this state knows.
      const st = (isObj(ev.data.state) ? ev.data.state : {}) as Loose;
      const status = st.status as JobStatus | undefined;
      s = {
        ...s,
        serverState: ev.data.state,
        ...(ev.data.status ? { serverStatus: ev.data.status } : {}),
        ...snapshotFields(ev.data.state),
        ...(typeof st.text === 'string' ? { text: st.text } : {}),
        ...(typeof st.reasoning === 'string' ? { reasoning: st.reasoning } : {}),
        ...(status && ['idle', 'running', 'waiting', 'completed', 'cancelled', 'error'].includes(status) ? { status } : {}),
      };
      if (s.status === 'idle' && (ev.data.status === 'running' || ev.data.status === 'queued')) s = { ...s, status: 'running' };
      break;
    }
    case 'run.finished': {
      const cancelled = ev.data.status === 'cancelled';
      s = {
        ...s,
        status: cancelled ? 'cancelled' : 'completed',
        textDone: true,
        finishedAt: at,
        model: ev.data.model ?? s.model,
        ...(ev.data.output !== undefined ? { output: ev.data.output } : {}),
        steps: s.steps.map((x) => (x.status === 'running' || x.status === 'waiting' ? { ...x, status: cancelled ? 'skipped' : 'ok' } : x)),
        approvals: s.approvals.map((a) => (a.status === 'pending' ? { ...a, status: 'expired' } : a)),
      };
      if (s.progress && !cancelled) s = { ...s, progress: { ...s.progress, percent: 100 } };
      break;
    }
    case 'run.error':
      s = {
        ...s,
        status: 'error',
        finishedAt: at,
        error: { code: ev.data.code, message: ev.data.message, retryable: ev.data.retryable },
        steps: s.steps.map((x) => (x.status === 'running' ? { ...x, status: 'error' } : x)),
      };
      break;
  }
  return s;
}

export function foldEvents(events: (AnyJobEvent | ModuleJobEvent)[], start: JobState = initialJobState()): JobState {
  return events.reduce(foldEvent, start);
}

export const isTerminal = (s: JobState) => s.status === 'completed' || s.status === 'cancelled' || s.status === 'error';

/* ---------- helpers for screens ---------- */

const PHASE_NAMES: Record<string, 'Plan' | 'Search' | 'Read' | 'Analyze' | 'Write' | 'Done'> = {
  plan: 'Plan', planning: 'Plan', review: 'Plan', search: 'Search', searching: 'Search', read: 'Read', reading: 'Read',
  analyze: 'Analyze', analysis: 'Analyze', verify: 'Analyze', write: 'Write', writing: 'Write', report: 'Write', done: 'Done', finished: 'Done',
};

/** Props for `ui/ResearchProgress` from a folded research job (phase names, counts, recent activity). */
export function researchProgressOf(s: JobState): { phase: 'Plan' | 'Search' | 'Read' | 'Analyze' | 'Write' | 'Done'; consulted: number; cited: number; feed: { id: string; text: string }[] } | null {
  const r = s.research;
  if (!r) return null;
  const phase = isTerminal(s) ? 'Done' : PHASE_NAMES[String(r.phase ?? '').toLowerCase()] ?? 'Search';
  const feed = (r.activity ?? []).slice(-5).map((a, i) => ({ id: `${a.ts ?? i}-${i}`, text: [a.action, a.title ?? a.domain].filter(Boolean).join(': ') }));
  return { phase, consulted: r.sourcesConsulted ?? 0, cited: r.sourcesCited ?? s.citations.filter((c) => c.cited !== false).length, feed };
}

/** The approvals still waiting for a decision. */
export const pendingApprovals = (s: JobState) => s.approvals.filter((a) => a.status === 'pending');
